// Sidebar — left-rail navigation for the v2 dashboard shell.
//
// 10 gradient groups locked at scaffold time per
// specs/tier-01-architecture/dashboard-v2.app-navigation.admin-dashboard-v2.yaml.
// Items expand as ε CHGs land; today most rows are forward-promised placeholders.
//
// Active route detection: findActiveGroup(pathname) — see nav.ts.
// Auto-expands the active group on mount.
//
// Per .forge/plans/v2-nav-shell.md.

import * as LucideIcons from "lucide-react";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { Link, useLocation } from "react-router-dom";
import { useBrand } from "../../contexts/BrandContext";
import { useAuth, type Account } from "../../contexts/AuthContext";
// CHG-1540 / ITER-3658 — IA correction. Sidebar swaps Platform vs.
// 9 org-admin groups for platform-admin accounts based on whether
// an org is "in context" via OrgSwitcher.
import { useOrg } from "../../contexts/OrgContext";
import { findActiveGroup, navGroups, type GroupId, type NavGroup } from "./nav";

// ─────────────────────────────────────────────────────────────────────
// CHG-1473 / ITER-3593 — visibleWhen evaluator.
//
// Tiny DSL — intentionally smaller than a real expression parser. Closes
// the P0 where donor accounts saw the entire admin nav (every clicked
// link 404'd because the donor has no admin routes). Supported grammar:
//
//   <atom>   := account.<field>                          (bare boolean read)
//             | account.<field> == (true|false)
//             | account.<field>.length (>|<|>=|<=|==|!=) <int>
//   <expr>   := <atom> ( ( && | || ) <atom> )*
//   precedence: || lowest, && higher; no parens
//
// If we ever need parens / negation / string compares we swap in a real
// parser (jsep). Until then this covers the two expressions live in
// nav.ts ("account.has_staff_role == true" + "account.has_donor_profile
// == true"). Defensive defaults: null account → false (fail-closed,
// admin nav stays hidden until /users/me hydrates); unknown atom shape
// → false + console.warn (never throws — the Sidebar must render).
// ─────────────────────────────────────────────────────────────────────

const ATOM_BOOL_FIELD_RE = /^account\.([A-Za-z_][A-Za-z0-9_]*)$/;
const ATOM_BOOL_CMP_RE =
  /^account\.([A-Za-z_][A-Za-z0-9_]*)\s*==\s*(true|false)$/;
const ATOM_LENGTH_RE =
  /^account\.([A-Za-z_][A-Za-z0-9_]*)\.length\s*(==|!=|>=|<=|>|<)\s*(-?\d+)$/;

function evalAtom(atom: string, account: Account | null): boolean {
  if (!account) return false;
  const acct = account as unknown as Record<string, unknown>;
  let m = ATOM_BOOL_CMP_RE.exec(atom);
  if (m) {
    const field = m[1];
    const expected = m[2] === "true";
    return Boolean(acct[field]) === expected;
  }
  m = ATOM_LENGTH_RE.exec(atom);
  if (m) {
    const field = m[1];
    const op = m[2];
    const rhs = Number(m[3]);
    const v = acct[field];
    const lhs = Array.isArray(v) || typeof v === "string" ? v.length : NaN;
    switch (op) {
      case "==":
        return lhs === rhs;
      case "!=":
        return lhs !== rhs;
      case ">":
        return lhs > rhs;
      case "<":
        return lhs < rhs;
      case ">=":
        return lhs >= rhs;
      case "<=":
        return lhs <= rhs;
    }
  }
  m = ATOM_BOOL_FIELD_RE.exec(atom);
  if (m) {
    return Boolean(acct[m[1]]);
  }
  if (typeof console !== "undefined") {
    console.warn(`[Sidebar] unknown visibleWhen atom: ${JSON.stringify(atom)}`);
  }
  return false;
}

export function evalVisibleWhen(
  expr: string | undefined,
  account: Account | null,
): boolean {
  if (!expr) return true;
  const orParts = expr.split(/\s+\|\|\s+/);
  for (const orPart of orParts) {
    const andParts = orPart.split(/\s+&&\s+/);
    if (andParts.every((atom) => evalAtom(atom.trim(), account))) return true;
  }
  return false;
}

type IconCmp = ComponentType<{
  size?: number;
  "aria-hidden"?: boolean | "true";
}>;

interface IconRendererProps {
  name?: string;
  size?: number;
}

function IconFor({ name, size = 16 }: IconRendererProps) {
  if (!name) return null;
  const pascal = name
    .split("-")
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
    .join("");
  const Cmp = (LucideIcons as unknown as Record<string, IconCmp | undefined>)[pascal];
  if (!Cmp) return null;
  return <Cmp size={size} aria-hidden="true" />;
}

export interface SidebarProps {
  collapsed?: boolean;
  // CHG-1427 / ITER-3547 — narrow-viewport mode. When true, the sidebar uses
  // drawer semantics: collapsed → width 0 (chrome reclaimed), expanded →
  // position:fixed overlay covering content with a backdrop. AppShell drives
  // this via useNarrowViewport(); default false preserves the existing
  // sticky-rail rendering for standalone tests and any non-AppShell caller.
  narrow?: boolean;
  // Called by the backdrop click when the sidebar is in overlay mode. Lets
  // the parent (AppShell) collapse the sidebar without the user having to
  // find the hamburger. Optional — without it, backdrop becomes a no-op.
  onCollapse?: () => void;
}

export function Sidebar({ collapsed = false, narrow = false, onCollapse }: SidebarProps) {
  const { chrome } = useBrand();
  const { account } = useAuth();
  // CHG-1540 / ITER-3658 — IA correction. Read the active org so the
  // Platform vs. org-admin groups can swap based on whether a
  // platform-admin has an org "in context". useOrg is safe to read here
  // (OrgProvider sits below AuthProvider; OrgSwitcher mounts inside the
  // same Sidebar so the dependency was already there).
  const { org: activeOrg } = useOrg();
  const location = useLocation();
  const activeMatch = findActiveGroup(location.pathname);
  const activeGroupId: GroupId | null = activeMatch?.group.id ?? null;
  const activeItemId: string | null = activeMatch?.item.id ?? null;

  // CHG-1473 — filter groups by visibleWhen against the authenticated
  // account. Without this, donor-only accounts see all 9 admin groups
  // and every click 404s. Re-computes on account change (login → admin,
  // logout → null → no admin groups).
  //
  // CHG-1540 — layered on top: for a platform-admin account, swap the
  // 9 org-admin groups vs. the Platform group based on activeOrg:
  //   - is_platform_admin && !activeOrg → ONLY Platform (+ optional My
  //     Giving if has_donor_profile). The 9 org-admin groups have no
  //     tenant context yet — rendering them would 403 or show empty
  //     data (the pre-fix bug the user reported).
  //   - is_platform_admin && activeOrg  → the 9 org-admin groups
  //     (Home item routes to /overview). Platform group is hidden so
  //     the admin is unambiguously "acting as" the active org's admin;
  //     OrgSwitcher's "Clear org" option returns them to /platform.
  // Non-platform-admin accounts ignore activeOrg entirely (their group
  // set is whatever evalVisibleWhen returns).
  const visibleGroups = useMemo(() => {
    const base = navGroups.filter((g) => evalVisibleWhen(g.visibleWhen, account));
    if (!account?.is_platform_admin) return base;
    return base.filter((g) => {
      const isPlatformGroup = g.id === "platform";
      const isMyGivingGroup = g.id === "mygiving";
      const isOrgAdminGroup = !isPlatformGroup && !isMyGivingGroup;
      if (!activeOrg && isOrgAdminGroup) return false;
      if (activeOrg && isPlatformGroup) return false;
      return true;
    });
  }, [account, activeOrg]);

  // CHG-1427 — derived render mode. Three behaviors:
  //   - "sticky" (wide viewport): existing 64/248px sticky rail
  //   - "drawer-closed" (narrow + collapsed): width:0 — chrome reclaimed
  //   - "drawer-open" (narrow + expanded): position:fixed overlay + backdrop
  // State persists across mode changes (expanded groups stay expanded).
  const drawerOpen = narrow && !collapsed;
  const drawerClosed = narrow && collapsed;

  // Track which groups are expanded. Auto-expand the active group on mount
  // and whenever the active group changes.
  const [expanded, setExpanded] = useState<Set<GroupId>>(() => {
    const s = new Set<GroupId>();
    if (activeGroupId) s.add(activeGroupId);
    return s;
  });
  useEffect(() => {
    if (activeGroupId) {
      setExpanded((prev) => {
        if (prev.has(activeGroupId)) return prev;
        const next = new Set(prev);
        next.add(activeGroupId);
        return next;
      });
    }
  }, [activeGroupId]);

  // CHG-1431 / ITER-3550 — ESC dismisses the overlay when in drawer-open
  // mode. Consistency with FormDialog + CommandPalette (both honor ESC).
  // Mouse users already have backdrop click + the TopBar hamburger
  // (z-index 800 > overlay 100, so it stays clickable); ESC closes the
  // gap for keyboard-driven users who'd otherwise have to Tab to the
  // hamburger or backdrop. Effect listens at the document level so we
  // catch ESC regardless of focus location inside the sidebar.
  useEffect(() => {
    if (!drawerOpen || !onCollapse) return;
    const handler = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        onCollapse();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [drawerOpen, onCollapse]);

  function toggleGroup(id: GroupId) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      {/* CHG-1427 — backdrop renders only in drawer-open mode (narrow viewport
          + expanded sidebar). Click dismisses via onCollapse. z-index 99 sits
          BETWEEN normal content and the overlaid sidebar (z-index 100) so the
          sidebar stays on top while the rest of the chrome is veiled. The
          CommandPalette modal at z-index 1000 still wins if both are open. */}
      {drawerOpen && (
        <div
          data-testid="sidebar-backdrop"
          aria-hidden="true"
          onClick={onCollapse}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99,
            // composition-lint-exempt: modal/overlay backdrop scrim — pending shared --color-scrim token (DRIFT-776)
            background: "rgba(15, 23, 42, 0.45)",
            cursor: "pointer",
          }}
        />
      )}
      <aside
        role="navigation"
        aria-label="Primary navigation"
        data-collapsed={collapsed}
        data-narrow={narrow}
        data-drawer-mode={drawerOpen ? "open" : drawerClosed ? "closed" : "sticky"}
        style={{
          // CHG-1427 — drawer-closed collapses to width 0; drawer-open keeps
          // 248 but as a fixed overlay; sticky modes keep the existing
          // 64/248 sticky-rail dimensions.
          width: drawerClosed ? 0 : collapsed ? 64 : 248,
          flexShrink: 0,
          background: "var(--color-surface)",
          borderRight: drawerClosed ? "none" : "1px solid var(--color-border-subtle)",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          position: drawerOpen ? "fixed" : "sticky",
          top: 0,
          left: drawerOpen ? 0 : undefined,
          zIndex: drawerOpen ? 100 : undefined,
          // CHG-1427 — boxShadow on overlay so the seam between sidebar and
          // backdrop is visible (consistent with FormDialog + CommandPalette).
          boxShadow: drawerOpen ? "var(--shadow-overlay, 0 25px 50px -12px rgba(0,0,0,0.25))" : undefined,
          overflowY: "auto",
          overflowX: "hidden",
          transition: "width 160ms ease",
        }}
      >
      <div
        style={{
          padding: collapsed ? "16px 8px" : "16px 20px",
          borderBottom: "1px solid var(--color-border-subtle)",
        }}
      >
        <span
          className="font-wordmark"
          style={{
            fontSize: collapsed ? 16 : 18,
            fontWeight: 600,
            color: "var(--color-primary)",
            letterSpacing: "-0.01em",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "var(--font-wordmark, 'Poppins', sans-serif)",
          }}
        >
          {collapsed ? (chrome.wordmark[0] ?? "·") : chrome.wordmark}
        </span>
      </div>
      <nav style={{ flex: 1, padding: 6 }}>
        {visibleGroups.map((group) => (
          <GroupBlock
            key={group.id}
            group={group}
            collapsed={collapsed}
            isExpanded={expanded.has(group.id)}
            isActive={group.id === activeGroupId}
            activeItemId={activeItemId}
            onToggle={() => toggleGroup(group.id)}
          />
        ))}
      </nav>
      <footer
        style={{
          padding: "10px 12px",
          borderTop: "1px solid var(--color-border-subtle)",
          fontSize: 11,
          color: "var(--color-text-muted)",
        }}
      >
        {/* CHG-1271 — surface 1: brand-specific footer tagline.
            Collapsed renders the wordmark's first character (matches the
            sidebar header's collapsed treatment). Expanded renders
            "<display_name> · <tagline>" from useBrand() — brand-specific
            on Mohseen / AloraChurch hostnames. */}
        {collapsed ? (chrome.wordmark[0] ?? "·") : `${chrome.display_name} · ${chrome.tagline}`}
      </footer>
      </aside>
    </>
  );
}

interface GroupBlockProps {
  group: NavGroup;
  collapsed: boolean;
  isExpanded: boolean;
  isActive: boolean;
  activeItemId: string | null;
  onToggle: () => void;
}

function GroupBlock({
  group,
  collapsed,
  isExpanded,
  isActive,
  activeItemId,
  onToggle,
}: GroupBlockProps) {
  // Gradient stripe color comes from the group's locked token; see tokens.css §3.5.
  const stripeColor = `var(--color-${group.gradientToken}-from)`;
  return (
    <div
      data-group-id={group.id}
      data-gradient-token={group.gradientToken}
      style={{ marginBottom: 2 }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={`nav-group-items-${group.id}`}
        title={collapsed ? group.title : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: collapsed ? "8px 10px" : "8px 12px",
          textAlign: "left",
          border: "none",
          borderLeft: `3px solid ${isActive ? stripeColor : "transparent"}`,
          background: isActive ? "var(--color-surface-hover)" : "transparent",
          color: "var(--color-text)",
          fontSize: 13,
          fontWeight: 500,
          fontFamily: "inherit",
          cursor: "pointer",
          borderRadius: "var(--radius-sm, 6px)",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            color: stripeColor,
            background: `${stripeColor}1a`,
            borderRadius: "var(--radius-sm, 6px)",
            flexShrink: 0,
          }}
        >
          <IconFor name={group.icon} size={14} />
        </span>
        {!collapsed && (
          <>
            <span style={{ flex: 1 }}>{group.title}</span>
            <IconFor name={isExpanded ? "chevron-down" : "chevron-right"} size={14} />
          </>
        )}
      </button>
      {isExpanded && !collapsed && (
        <ul
          id={`nav-group-items-${group.id}`}
          role="list"
          style={{
            listStyle: "none",
            padding: 0,
            margin: "2px 0 4px 0",
          }}
        >
          {group.items.map((item) => {
            const itemActive = item.id === activeItemId;
            const rowStyle = {
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 12px 6px 36px",
              fontSize: 12.5,
              color: item.forwardPromised
                ? "var(--color-text-muted)"
                : itemActive
                ? "var(--color-text)"
                : "var(--color-text-secondary)",
              textDecoration: "none",
              borderRadius: "var(--radius-sm, 6px)",
              background: itemActive ? "var(--color-surface-hover)" : "transparent",
              fontWeight: itemActive ? 500 : 400,
              cursor: item.forwardPromised ? "not-allowed" : "pointer",
            } as const;
            const rowContents = (
              <>
                <IconFor name={item.icon} size={13} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.forwardPromised && (
                  <span
                    style={{
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "var(--color-text-muted)",
                      border: "1px solid var(--color-border-subtle)",
                      borderRadius: 4,
                      padding: "1px 5px",
                    }}
                  >
                    Soon
                  </span>
                )}
              </>
            );
            return (
              <li key={item.id}>
                {item.forwardPromised ? (
                  // CHG-1473 — forwardPromised items render as a non-
                  // clickable <span> instead of <Link>. The route the
                  // nav declares may not exist in App.tsx yet (would
                  // hit the NotFound page); keeping the row visible
                  // for discoverability while disabling navigation
                  // prevents the user from clicking into a 404.
                  <span
                    role="presentation"
                    data-item-id={item.id}
                    data-forward-promised="true"
                    aria-disabled="true"
                    title={`${item.label} — coming soon`}
                    style={rowStyle}
                  >
                    {rowContents}
                  </span>
                ) : (
                  <Link
                    to={item.route}
                    data-item-id={item.id}
                    aria-current={itemActive ? "page" : undefined}
                    style={rowStyle}
                  >
                    {rowContents}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
